import {useEffect, useRef, useState} from "react";
import styled from "styled-components";
import {guestErrorMessage} from "api/guest.api";
import {Alert, AlertIcon, Input, PillButton, PillButtonOutline, Spinner, tokens} from "ui/blocks/guestMessaging/GuestUI";
import {digitsOnly} from "./useQuickBookingAuth";

export const RESEND_SECONDS = 30;

/**
 * 4-digit code entry (quick booking and code login). onVerify(code) and onResend() are
 * async; onVerify throws on a wrong or expired code, onResend returns true
 * when a new code was sent.
 */
export const CodeEntry = ({
    phone, onVerify, onResend, onChangeNumber,
    text = "Enter the 4-digit code we sent to your phone to confirm your booking.",
    confirmLabel = "Confirm booking", busyLabel = "Confirming…", changeLabel = "Change number",
}) => {
    const [code, setCode] = useState("");
    const [error, setError] = useState("");
    const [verifying, setVerifying] = useState(false);
    const [wait, setWait] = useState(RESEND_SECONDS);
    const inputRef = useRef(null);
    const busyRef = useRef(false);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    useEffect(() => {
        if (wait <= 0) return undefined;
        const t = setTimeout(() => setWait((s) => s - 1), 1000);
        return () => clearTimeout(t);
    }, [wait]);

    const verify = async (value) => {
        if (busyRef.current || value.length !== 4) return;
        busyRef.current = true;
        setVerifying(true);
        setError("");
        try {
            await onVerify(value);
        } catch (err) {
            setError(guestErrorMessage(err, "We couldn't confirm the code. Try again."));
            setCode("");
            inputRef.current?.focus();
        } finally {
            busyRef.current = false;
            setVerifying(false);
        }
    };

    const onChange = (e) => {
        const value = digitsOnly(e.target.value).slice(0, 4);
        setCode(value);
        setError("");
        if (value.length === 4) verify(value);
    };

    const resend = async () => {
        setError("");
        setCode("");
        if (await onResend()) setWait(RESEND_SECONDS);
    };

    const last4 = digitsOnly(phone).slice(-4);

    return (
        <Wrap>
            <Text>{text}</Text>
            {last4 && <Sent>Sent to (•••) •••-{last4}</Sent>}
            <CodeInput
                ref={inputRef}
                aria-label="4-digit code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={4}
                value={code}
                onChange={onChange}
                disabled={verifying}
                aria-invalid={Boolean(error)}
            />
            {error && <Alert role="alert"><AlertIcon/><span>{error}</span></Alert>}
            <PillButton type="button" $block disabled={verifying || code.length !== 4} onClick={() => verify(code)}>
                {verifying ? <><Spinner $light $size={16}/> {busyLabel}</> : confirmLabel}
            </PillButton>
            <Row>
                <PillButtonOutline type="button" onClick={resend} disabled={wait > 0 || verifying}>
                    {wait > 0 ? `Resend code in ${wait}s` : "Resend code"}
                </PillButtonOutline>
                <TextButton type="button" onClick={onChangeNumber} disabled={verifying}>{changeLabel}</TextButton>
            </Row>
        </Wrap>
    );
};

const Wrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    font-family: var(--font-primary);
`;

const Text = styled.p`
    margin: 0;
    font-size: 15px;
    line-height: 1.5;
    color: ${tokens.text};
`;

const Sent = styled.p`
    margin: -6px 0 0;
    font-size: 13px;
    color: ${tokens.muted};
`;

const CodeInput = styled(Input)`
    text-align: center;
    font-size: 28px;
    font-weight: 600;
    letter-spacing: 0.6em;
    padding-left: 0.6em;
`;

const Row = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
`;

const TextButton = styled.button`
    background: none;
    border: 0;
    padding: 8px 0;
    font: inherit;
    font-size: 14px;
    color: ${tokens.muted};
    text-decoration: underline;
    cursor: pointer;
`;
